import { injectIntl } from "react-intl";
import React, { useCallback, useEffect, useRef, useState } from "react";
import MultiSelect from "src/components/MultiSelect";
import SingleSelect from "src/components/SingleSelect";
import SingleSelectWithToggle from "src/components/SingleSelectWithToggle";
import apiService from "src/services/api.service";
import { OperationType } from "src/helpers/Enums";
import { toast } from "react-toastify";

const NewOffer = (props) => {
  const operationType = props.type || OperationType.CREATE;
  const isUpdate = operationType === OperationType.UPDATE;
  const [openSelect, setOpenSelect] = useState(false);
  const [openSourceSelect, setOpenSourceSelect] = useState(false);
  const [openEverflowSelect, setOpenEverflowSelect] = useState(false);
  const [selectedTags, setSelectedTags] = useState(
    isUpdate && props.offer?.tag_ids ? props.offer.tag_ids : []
  );
  const [offer, setOffer] = useState(
    isUpdate && props.offer
      ? {
          ...props.offer,
          name: props.offer.name || "",
          tags: props.offer.tag_ids?.map((item) => item._id) || [],
        }
      : {
          name: "",
          tags: [],
          creativeName: "",
          note: "",
        }
  );
  const [tags, setTags] = useState([]);
  const divRef = useRef(null);
  const sourceRef = useRef(null);

  // Initialize source based on offer linkType
  const getInitialSource = () => {
    if (!isUpdate) {
      return { id: 0, name: "No Linked Source" };
    }
    if (props.offer.link_type === "OFFER") {
      return { id: 1, name: "Everflow" };
    }
    if (props.offer.link_type === "LISTICLE") {
      return { id: 2, name: "Listecles.ai" };
    }
    return { id: 0, name: "No Linked Source" };
  };

  const [selectedSource, setSelectedSource] = useState(getInitialSource());
  const sourceList = [
    { id: 0, name: "No Linked Source" },
    { id: 1, name: "Everflow" },
    { id: 2, name: "Listecles.ai" },
  ];
  const [sourceInputValue, setSourceInputValue] = useState(
    isUpdate && props.offer?.link_id ? props.offer.link_id : ""
  );
  const [everflowOfferList, setEverflowOfferList] = useState([]);
  const [searchedEverflowOfferList, setSearchedEverflowOfferList] = useState(
    []
  );
  const [selectedEverflowOffer, setSelectedEverflowOffer] = useState();
  const [everflowSearch, setEverflowSearch] = useState("");
  const [everflowPage, setEverflowPage] = useState(0);
  const [everflowPagination, setEverflowPagination] = useState(null);

  const [listicleCampaignList, setListicleCampaignList] = useState([]);
  const [searchedListicleCampaignList, setSearchedListicleCampaignList] =
    useState([]);
  const [selectedListicleCampaign, setSelectedListicleCampaign] = useState();
  const [listicleSearch, setListicleSearch] = useState("");
  const [listiclePage, setListiclePage] = useState(0);
  const [listiclePagination, setListiclePagination] = useState(null);
  const [openListicleSelect, setOpenListicleSelect] = useState(false);

  // Show already linked offer/campaign in Edit mode (link_id / link_name from API)
  useEffect(() => {
    if (!isUpdate || !props.offer?.link_id) return;
    if (props.offer.link_type === "LISTICLE") {
      setSelectedListicleCampaign({
        _id: props.offer.link_id,
        id: props.offer.link_id,
        name: props.offer.link_name || props.offer.link_id,
      });
    } else if (props.offer.link_type === "OFFER") {
      setSelectedEverflowOffer({
        _id: props.offer.link_id,
        network_offer_id: props.offer.link_id,
        name: props.offer.link_name || props.offer.link_id,
      });
    }
  }, [
    isUpdate,
    props.offer?.link_id,
    props.offer?.link_type,
    props.offer?.link_name,
  ]);

  useEffect(() => {
    apiService.getAllTags().then((response) => {
      setTags(response.data);
      // In update mode, map tag_ids to full tag objects
      if (isUpdate && props.offer?.tag_ids && response.data) {
        const fullTagObjects = props.offer.tag_ids
          .map((tagIdOrObj) => {
            // If it's already a full object, use it; otherwise find by _id
            if (tagIdOrObj._id && tagIdOrObj.name) {
              return tagIdOrObj;
            }
            return response.data.find(
              (tag) => tag._id === tagIdOrObj || tag._id === tagIdOrObj._id
            );
          })
          .filter(Boolean); // Remove any undefined values
        setSelectedTags(fullTagObjects);
      }
    });
  }, [isUpdate, props.offer?.tag_ids]);

  // Load unrecorded Everflow offers when "Select from Everflow" is opened or page changes
  useEffect(() => {
    if (selectedSource.id === 1 && openEverflowSelect) {
      apiService
        .getEverflowUnrecordedOffers({ page: everflowPage, limit: 7 })
        .then((res) => {
          const raw = res?.data?.data?.offers || [];
          const pagination = res?.data?.data?.pagination || null;
          const mapped = raw.map((o) => ({
            ...o,
            _id: o.network_offer_id,
            is_enabled: o.offer_status === "active",
          }));
          setEverflowOfferList(mapped);
          setSearchedEverflowOfferList(mapped);
          setEverflowPagination(pagination);
        });
    }
  }, [selectedSource.id, openEverflowSelect, everflowPage]);

  // Load listicle campaigns when "Select from Listecles.ai" is opened or page changes
  useEffect(() => {
    if (selectedSource.id === 2 && openListicleSelect) {
      apiService
        .getListicleCampaigns({ page: listiclePage + 1, limit: 7 })
        .then((res) => {
          const raw = res?.data?.data?.campaigns || [];
          const data = res?.data?.data;
          const mapped = raw.map((c) => ({
            ...c,
            _id: c.id,
          }));
          setListicleCampaignList(mapped);
          setSearchedListicleCampaignList(mapped);
          setListiclePagination(
            data
              ? {
                  totalPages: data.totalPages ?? 1,
                  total: data.total,
                  page: data.page,
                  limit: data.limit,
                }
              : null
          );
        });
    }
  }, [selectedSource.id, openListicleSelect, listiclePage]);

  // Filter listicle campaigns by search term
  useEffect(() => {
    if (!listicleCampaignList || listicleCampaignList.length === 0) {
      setSearchedListicleCampaignList([]);
      return;
    }
    const filtered = listicleCampaignList.filter((item) => {
      const itemName = item?.name?.toLowerCase?.() || "";
      const searchTerm = listicleSearch?.toLowerCase?.() || "";
      return itemName.includes(searchTerm);
    });
    setSearchedListicleCampaignList(filtered || []);
  }, [listicleSearch, listicleCampaignList]);

  // Filter Everflow offers by search term
  useEffect(() => {
    if (!everflowOfferList || everflowOfferList.length === 0) {
      setSearchedEverflowOfferList([]);
      return;
    }
    const filtered = everflowOfferList.filter((item) => {
      const itemName = item?.name?.toLowerCase?.() || "";
      const searchTerm = everflowSearch?.toLowerCase?.() || "";
      return itemName.includes(searchTerm);
    });
    setSearchedEverflowOfferList(filtered || []);
  }, [everflowSearch, everflowOfferList]);

  useEffect(() => {
    setOffer({ ...offer, tags: selectedTags?.map((item) => item._id) });
  }, [selectedTags]);

  const handleClickOutside = useCallback((event) => {
    if (divRef.current && !divRef.current.contains(event.target)) {
      setOpenSelect(false);
    }
  }, []);

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [handleClickOutside]);

  const handleClickSourceOutside = useCallback((event) => {
    if (sourceRef.current && !sourceRef.current.contains(event.target)) {
      setOpenSourceSelect(false);
    }
  }, []);

  useEffect(() => {
    document.addEventListener("mousedown", handleClickSourceOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickSourceOutside);
    };
  }, [handleClickSourceOutside]);

  const createUpdateOffer = () => {
    const offerData = {
      ...offer,
      name: offer.name,
      linkType:
        selectedSource.id !== 0
          ? selectedSource.id === 1
            ? "OFFER"
            : "LISTICLE"
          : null,
      linkId: selectedSource.id !== 0 ? sourceInputValue?.trim() : null,
    };

    const handleResponse = (response) => {
      // Check if response indicates an error (due to axios interceptor returning error.response)
      // If status is 400+, it's an error
      if (response && response.status && response.status >= 400) {
        // Error response from interceptor
        handleError(response);
      } else if (response && response.data) {
        // Success - check if data indicates success
        props.closeModal();
      } else {
        // Success
        props.closeModal();
      }
    };

    const handleError = (error) => {
      let errorMessage = isUpdate
        ? "An error occurred while updating the offer."
        : "An error occurred while creating the offer.";

      // Try different paths for error message
      const errorData = error?.data;
      if (errorData?.message) {
        errorMessage = errorData.message;
      } else if (typeof errorData === "string") {
        errorMessage = errorData;
      } else if (errorData?.error?.message) {
        errorMessage = errorData.error.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }

      const toastId = toast.error(errorMessage, {
        toastId: `error-${Date.now()}-${Math.random()}`,
        position: "bottom-right",
        autoClose: 5000,
        hideProgressBar: true,
        closeOnClick: true,
      });

      // Ensure toast closes after 5 seconds
      setTimeout(() => {
        toast.dismiss(toastId);
      }, 5000);
    };

    if (operationType === OperationType.CREATE) {
      apiService.createOffer(offerData).then(handleResponse).catch(handleError);
    } else {
      apiService.updateOffer(offerData).then(handleResponse).catch(handleError);
    }
  };

  const toggleTag = (tag) => {
    setSelectedTags(
      selectedTags?.find((item) => item._id === tag._id)
        ? selectedTags.filter((t) => t._id !== tag._id)
        : [...selectedTags, tag]
    );
  };

  const handleInput = (param, value) => {
    setOffer({ ...offer, [param]: value });
  };

  const isEnableButton = () => {
    const offerName = offer?.name || "";
    const hasBasicFields = offerName && selectedTags?.length > 0;

    if (selectedSource.id === 0) {
      return hasBasicFields;
    }

    return hasBasicFields && sourceInputValue?.trim();
  };

  const selectSource = (source) => {
    setSelectedSource(source);
    setSourceInputValue("");
    setSelectedEverflowOffer(undefined);
    setSelectedListicleCampaign(undefined);
    setEverflowSearch("");
    setListicleSearch("");
    setOpenEverflowSelect(false);
    setOpenListicleSelect(false);
  };

  return (
    <div className="NewStrategyModalOverlay z-[99]">
      <div className="NewStrategyModal">
        <div className={"w-full flex justify-between"}>
          <div
            className={
              operationType === OperationType.CREATE
                ? "NewStrategy"
                : "NewCreative"
            }
          >
            <img
              alt={"Logo"}
              src={
                isUpdate
                  ? "/media/icons/editIcon.svg"
                  : "/media/icons/purplePlusIcon.svg"
              }
              className={isUpdate ? "w-4 h-4" : "w-[18px] h-[18px]"}
            />
            {isUpdate ? "Edit" : "New"} Offer
          </div>
          <button className={"CloseIconButton"} onClick={props.closeModal}>
            <img
              alt={"Logo"}
              src={"/media/icons/closeIcon.svg"}
              className={"w-5 h-5"}
            />
          </button>
        </div>
        <div className={"NewStrategyInputSection"}>
          <div className={"NewStrategyInputTitle"}>Offer Name</div>
          <input
            className={"NewStrategyInput"}
            type="text"
            placeholder="Enter"
            value={offer?.name || ""}
            onChange={(e) => handleInput("name", e.target.value)}
          />
        </div>
        <div className={"NewStrategyInputSection"}>
          <div
            className={"NewStrategyInputTitle"}
            onClickCapture={(event) => handleClickOutside(event)}
          >
            Tag
          </div>
          {openSelect ? (
            <MultiSelect
              setOpenSelect={setOpenSelect}
              selectedItems={selectedTags}
              selectItem={toggleTag}
              list={tags}
              divRef={divRef}
            ></MultiSelect>
          ) : (
            <button className={"TagSelect"} onClick={() => setOpenSelect(true)}>
              {selectedTags?.length > 0 ? (
                <div className={"SelectedTags"}>
                  {selectedTags.map((tag) => (
                    <div key={tag._id} className="SelectedTag">
                      {tag.name}
                      <button
                        type="button"
                        className="ml-2 text-indigo-700 hover:text-indigo-900"
                        onClick={() => toggleTag(tag)}
                      >
                        <img
                          alt={"Logo"}
                          src={"/media/icons/closeIcon.svg"}
                          className={"w-[18px] h-[18px]"}
                        />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                "Select"
              )}
              <img
                alt={"Logo"}
                src={"/media/icons/chevronDown.svg"}
                className={"w-[18px] h-[18px]"}
              />
            </button>
          )}
        </div>
        <div className={"NewStrategyInputSection"} ref={sourceRef}>
          <div className={"NewStrategyInputTitle"}>Offer Source</div>
          {openSourceSelect ? (
            <SingleSelect
              setOpenSelect={setOpenSourceSelect}
              selectedItem={selectedSource}
              selectItem={(source) => {
                selectSource(source);
                setOpenSourceSelect(false);
              }}
              list={sourceList}
              noIcon
            />
          ) : (
            <button
              className={"TagSelect"}
              onClick={() => setOpenSourceSelect(true)}
            >
              {selectedSource ? selectedSource.name : "Select"}
              <img
                alt={"Logo"}
                src={"/media/icons/chevronDown.svg"}
                className={"w-[18px] h-[18px]"}
              />
            </button>
          )}
        </div>

        {selectedSource.id !== 0 &&
          !selectedEverflowOffer &&
          !selectedListicleCampaign && (
            <div className={"NewStrategyInputSection"}>
              <div className={"flex items-center gap-1"}>
                <img
                  alt={"Logo"}
                  src={"/media/icons/info-circle.svg"}
                  className={"w-[14px] h-[14px]"}
                />
                <div
                  className="text-[#8185A8] text-[12px] font-medium leading-normal"
                  style={{ fontFamily: "Inter" }}
                >
                  {selectedSource.id === 1
                    ? "Select an existing offer from Everflow to link with this one."
                    : "Select an existing offer from Listecles.ai to link with this one."}
                </div>
              </div>
              <button
                type="button"
                className="flex h-9 px-[10px] py-[7px] justify-center items-center gap-[10px] self-stretch rounded-lg border border-[#D9D9D9] bg-white text-[#171717] text-[12px] font-semibold leading-normal"
                style={{ fontFamily: "Inter" }}
                onClick={() => {
                  if (selectedSource.id === 1) {
                    setEverflowPage(0);
                    setOpenEverflowSelect(true);
                  } else {
                    setListiclePage(0);
                    setOpenListicleSelect(true);
                  }
                }}
              >
                <img
                  alt={"Logo"}
                  src={
                    selectedSource.id === 1
                      ? "/media/icons/everflow_logo.svg"
                      : "/media/icons/Union.svg"
                  }
                  className={
                    selectedSource.id === 1
                      ? "w-[17px] h-[16px]"
                      : "w-[14px] h-[16px]"
                  }
                />
                {selectedEverflowOffer?.name
                  ? selectedEverflowOffer.name
                  : selectedListicleCampaign?.name
                  ? selectedListicleCampaign.name
                  : selectedSource.id === 1
                  ? "Select from Everflow"
                  : "Select from Listecles.ai"}
              </button>
            </div>
          )}

        {/* Popup: render when open regardless of whether an offer is already selected (so "Change" works) */}
        {openEverflowSelect && selectedSource.id === 1 && (
          <SingleSelectWithToggle
            list={searchedEverflowOfferList}
            selectedItem={selectedEverflowOffer}
            setSelectedItem={(item) => {
              setSelectedEverflowOffer(item);
              const linkId = item?.network_offer_id ?? item?._id;
              if (linkId != null) {
                setSourceInputValue(String(linkId));
              }
            }}
            close={() => setOpenEverflowSelect(false)}
            img={"/media/icons/offer.svg"}
            pauseImg={"/media/icons/package.svg"}
            playImg={"/media/icons/package.svg"}
            title={"Everflow Offers"}
            titleImg={"/media/icons/offerPurple.svg"}
            search={everflowSearch}
            setSearch={setEverflowSearch}
            everflow={true}
            pagination={everflowPagination}
            currentPage={everflowPage + 1}
            onPageChange={(page) => setEverflowPage(page - 1)}
          />
        )}
        {openListicleSelect && selectedSource.id === 2 && (
          <SingleSelectWithToggle
            list={searchedListicleCampaignList}
            selectedItem={selectedListicleCampaign}
            setSelectedItem={(item) => {
              setSelectedListicleCampaign(item);
              const linkId = item?.id ?? item?._id;
              if (linkId != null) {
                setSourceInputValue(String(linkId));
              }
            }}
            close={() => setOpenListicleSelect(false)}
            img={"/media/icons/Union.svg"}
            pauseImg={"/media/icons/rocket-02.svg"}
            playImg={"/media/icons/rocket-02.svg"}
            title={"Listecles.ai Campaigns"}
            titleImg={"/media/icons/Union.svg"}
            search={listicleSearch}
            setSearch={setListicleSearch}
            everflow={true}
            pagination={listiclePagination}
            currentPage={listiclePage + 1}
            onPageChange={(page) => setListiclePage(page - 1)}
          />
        )}
        {selectedSource.id !== 0 &&
          (selectedEverflowOffer || selectedListicleCampaign) && (
            <div className={"flex flex-col gap-2 w-full min-w-0"}>
              <div className={"ToggleButtonLine min-w-0"}>
                <div className={"flex gap-[6px] items-center min-w-0 flex-1"}>
                  <img
                    alt={"Logo"}
                    src={
                      selectedEverflowOffer?.is_enabled
                        ? "/media/icons/package.svg"
                        : "/media/icons/rocket-02.svg"
                    }
                    className={"w-[14px] h-[14px] flex-shrink-0"}
                  />
                  <div
                    className={"flex flex-col gap-[6px] items-start min-w-0"}
                  >
                    <div className="text-[#15151F] font-['Inter'] text-xs font-semibold leading-[14px]">
                      {selectedEverflowOffer?.name ||
                        selectedListicleCampaign?.name}
                    </div>
                    <div className="text-[#8185A8] font-['Inter'] text-xs font-normal leading-[14px]">
                      {selectedEverflowOffer?.network_offer_id ||
                        selectedListicleCampaign?.id}
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  className="flex w-fit py-[7px] px-[10px] justify-center items-center gap-1 self-stretch rounded-lg border border-[#D9D9D9] bg-white text-[#171717] font-['Inter'] text-xs font-semibold leading-normal"
                  onClick={() => {
                    if (selectedSource.id === 1) {
                      setEverflowPage(0);
                      setOpenEverflowSelect(true);
                    } else {
                      setListiclePage(0);
                      setOpenListicleSelect(true);
                    }
                  }}
                >
                  <img
                    alt={"Logo"}
                    src={
                      selectedSource.id === 1
                        ? "/media/icons/everflow_logo.svg"
                        : "/media/icons/Union.svg"
                    }
                    className={"w-[17px] h-[16px]"}
                  />
                  Change
                </button>
              </div>
              <div className={"HorizontalDivider"}></div>
            </div>
          )}
        <div className="modal-actions">
          <button
            type="button"
            onClick={props.closeModal}
            className="CloseButton"
          >
            Close
          </button>
          <button
            type="submit"
            className="CreateButton"
            disabled={!isEnableButton()}
            onClick={createUpdateOffer}
          >
            {isUpdate ? "Save" : "Create"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default injectIntl(NewOffer);
